// ---------------------------------------------------------------------------
// C74: SEO Panel — editable meta + live scoring in page editor
// ---------------------------------------------------------------------------
import { useState, useEffect, useId, useRef } from "react";
import { Search, AlertTriangle, AlertCircle, Info, CheckCircle, ChevronDown, ChevronRight, Code2, Copy, Check } from "lucide-react";
import { analyzeSeo, optimizeSeoDraft, type SeoMeta, type SeoIssue, type SeoResult, type PuckComponent } from "@/lib/seo-engine";

interface SeoEditorPanelProps {
  meta: SeoMeta;
  onMetaChange: (meta: SeoMeta) => void;
  components: PuckComponent[];
  onComponentsChange?: (components: PuckComponent[]) => void;
}

const SEVERITY_ICON: Record<string, typeof AlertCircle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_COLOR: Record<string, string> = {
  error: "text-[var(--color-destructive)]",
  warning: "text-[var(--color-warning)]",
  info: "text-[var(--color-muted-foreground)]",
};

const SEVERITY_BG: Record<string, string> = {
  error: "bg-[var(--color-destructive)]/5",
  warning: "bg-[var(--color-warning)]/5",
  info: "bg-[var(--color-muted)]/50",
};

function ScoreRing({ score }: { score: number }) {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "var(--color-success)" : score >= 50 ? "var(--color-warning)" : "var(--color-destructive)";

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="88" height="88" viewBox="0 0 88 88">
        <circle cx="44" cy="44" r={radius} fill="none" stroke="var(--color-border)" strokeWidth="6" />
        <circle
          cx="44" cy="44" r={radius}
          fill="none" stroke={color} strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 44 44)"
          style={{ transition: "stroke-dashoffset 600ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold" style={{ color }}>{score}</span>
        <span className="text-[9px] text-[var(--color-muted-foreground)]">SEO Score</span>
      </div>
    </div>
  );
}

function IssueItem({ issue }: { issue: SeoIssue }) {
  const Icon = SEVERITY_ICON[issue.severity] ?? Info;
  return (
    <div className={`flex items-start gap-2 rounded-md px-2.5 py-2 text-xs ${SEVERITY_BG[issue.severity]}`}>
      <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${SEVERITY_COLOR[issue.severity]}`} />
      <div className="min-w-0">
        <p className="font-medium text-[var(--color-foreground)]">{issue.message}</p>
        <p className="text-[var(--color-muted-foreground)] mt-0.5">{issue.fix}</p>
      </div>
    </div>
  );
}

export default function SeoEditorPanel({ meta, onMetaChange, components, onComponentsChange }: SeoEditorPanelProps) {
  const [result, setResult] = useState<SeoResult>({
    score: 0,
    issues: [],
    schemaOrg: {},
    recommendations: [],
    autoFixableRecommendationIds: [],
    metaSuggestion: { title: "", description: "" },
  });
  const [showSchema, setShowSchema] = useState(false);
  const [copied, setCopied] = useState(false);
  const [optimizationStatus, setOptimizationStatus] = useState<string | null>(null);
  const titleId = useId();
  const descId = useId();
  const slugId = useId();
  const ogId = useId();
  const keywordId = useId();

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setResult(analyzeSeo(meta, components));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [meta, components]);

  const handleCopySchema = () => {
    const json = JSON.stringify(result.schemaOrg, null, 2);
    navigator.clipboard.writeText(`<script type="application/ld+json">\n${json}\n</script>`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => { /* clipboard blocked */ });
  };

  const handleAutoOptimize = () => {
    const optimization = optimizeSeoDraft(meta, components, {
      includeComponentFixes: Boolean(onComponentsChange),
    });

    if (optimization.applied.length === 0) {
      setOptimizationStatus("No deterministic SEO fixes are available for this draft.");
      setResult(optimization.analysis);
      return;
    }

    onMetaChange(optimization.meta);
    if (onComponentsChange) {
      onComponentsChange(optimization.components);
    }
    setResult(optimization.analysis);
    setOptimizationStatus(
      `Applied ${optimization.applied.length} SEO fix${optimization.applied.length === 1 ? "" : "es"} to this draft.`,
    );
  };

  const errors = result.issues.filter((i) => i.severity === "error");
  const warnings = result.issues.filter((i) => i.severity === "warning");
  const infos = result.issues.filter((i) => i.severity === "info");
  const autoFixableCount = result.autoFixableRecommendationIds.length;

  return (
    <div className="p-4 space-y-5 overflow-y-auto h-full">
      {/* Score */}
      <div className="flex flex-col items-center gap-1">
        <ScoreRing score={result.score} />
        <div className="flex items-center gap-3 text-[10px] text-[var(--color-muted-foreground)] mt-1">
          {errors.length > 0 && <span className="text-[var(--color-destructive)]">{errors.length} errors</span>}
          {warnings.length > 0 && <span className="text-[var(--color-warning)]">{warnings.length} warnings</span>}
          {infos.length > 0 && <span>{infos.length} tips</span>}
          {result.issues.length === 0 && (
            <span className="flex items-center gap-1 text-[var(--color-success)]">
              <CheckCircle className="h-3 w-3" /> Perfect
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-[var(--color-foreground)]">Auto-optimization</p>
            <p className="text-[10px] text-[var(--color-muted-foreground)]">
              Applies deterministic SEO fixes for meta tags, schema, slugs, focus keywords, alt text, and headings.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAutoOptimize}
            disabled={autoFixableCount === 0}
            className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-[11px] font-semibold text-[var(--color-primary-foreground)] transition-colors hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Auto-optimize SEO
          </button>
        </div>
        <p className="text-[10px] text-[var(--color-muted-foreground)]">
          {autoFixableCount > 0
            ? `${autoFixableCount} deterministic SEO fix${autoFixableCount === 1 ? "" : "es"} available.`
            : "No deterministic SEO fixes available right now."}
        </p>
        {optimizationStatus ? (
          <p className="text-[10px] font-medium text-[var(--color-foreground)]">{optimizationStatus}</p>
        ) : null}
      </div>

      {/* Meta fields */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase flex items-center gap-1.5">
          <Search className="h-3 w-3" /> Meta Tags
        </h4>

        <div>
          <label htmlFor={titleId} className="block text-[10px] font-medium text-[var(--color-muted-foreground)] mb-0.5">
            Title ({meta.title.length}/60)
          </label>
          <input
            id={titleId}
            type="text"
            value={meta.title}
            onChange={(e) => onMetaChange({ ...meta, title: e.target.value })}
            placeholder="Page title for search engines"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>

        <div>
          <label htmlFor={descId} className="block text-[10px] font-medium text-[var(--color-muted-foreground)] mb-0.5">
            Description ({meta.description.length}/155)
          </label>
          <textarea
            id={descId}
            value={meta.description}
            onChange={(e) => onMetaChange({ ...meta, description: e.target.value })}
            placeholder="Compelling description for search results"
            rows={3}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>

        <div>
          <label htmlFor={slugId} className="block text-[10px] font-medium text-[var(--color-muted-foreground)] mb-0.5">
            URL Slug
          </label>
          <input
            id={slugId}
            type="text"
            value={meta.slug}
            onChange={(e) => onMetaChange({ ...meta, slug: e.target.value })}
            placeholder="my-page-title"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>

        <div>
          <label htmlFor={ogId} className="block text-[10px] font-medium text-[var(--color-muted-foreground)] mb-0.5">
            OG Image URL
          </label>
          <input
            id={ogId}
            type="text"
            value={meta.ogImage}
            onChange={(e) => onMetaChange({ ...meta, ogImage: e.target.value })}
            placeholder="https://example.com/image.jpg"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>

        <div>
          <label htmlFor={keywordId} className="block text-[10px] font-medium text-[var(--color-muted-foreground)] mb-0.5">
            Focus Keyword
          </label>
          <input
            id={keywordId}
            type="text"
            value={meta.focusKeyword}
            onChange={(e) => onMetaChange({ ...meta, focusKeyword: e.target.value })}
            placeholder="e.g., yoga studio"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>
      </div>

      {/* Google Preview */}
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase mb-2">Search Preview</h4>
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 space-y-0.5">
          <p className="text-[11px] text-[var(--color-muted-foreground)] truncate">
            example.com/{meta.slug || "page"}
          </p>
          <p className="text-sm font-medium text-[#1a0dab] dark:text-[var(--color-primary)] truncate">
            {meta.title || "Untitled Page"}
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] line-clamp-2">
            {meta.description || "No description set. Search engines will auto-generate one from your page content."}
          </p>
        </div>
      </div>

      {/* Issues */}
      {result.issues.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase mb-2">Issues & Suggestions</h4>
          <div className="space-y-1.5">
            {errors.map((i) => <IssueItem key={i.id} issue={i} />)}
            {warnings.map((i) => <IssueItem key={i.id} issue={i} />)}
            {infos.map((i) => <IssueItem key={i.id} issue={i} />)}
          </div>
        </div>
      )}

      {/* Schema.org JSON-LD */}
      <div>
        <button
          onClick={() => setShowSchema(!showSchema)}
          className="flex items-center gap-1.5 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase hover:text-[var(--color-foreground)] transition-colors w-full"
        >
          {showSchema ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <Code2 className="h-3 w-3" />
          Schema.org JSON-LD
        </button>
        {showSchema && (
          <div className="mt-2 relative">
            <pre className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3 text-[10px] text-[var(--color-foreground)] overflow-x-auto max-h-48">
              {JSON.stringify(result.schemaOrg, null, 2)}
            </pre>
            <button
              onClick={handleCopySchema}
              className="absolute top-2 right-2 rounded-md p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
              title="Copy schema"
            >
              {copied ? <Check className="h-3 w-3 text-[var(--color-success)]" /> : <Copy className="h-3 w-3" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
